import { useMemo, useState, type PointerEvent } from 'react';
import { Tooltip } from './ChartFrame';
import { fmtDateTime, fmtTime, linearScale, nearestIndex, timeTicks, useWidth } from './scale';

export interface StackPoint {
  t: number;
  values: number[];
}

interface Props {
  data: StackPoint[];
  labels: string[];
  colors: string[];
  format: (v: number) => string;
  height?: number;
  max?: number;
}

const M = { top: 10, right: 12, bottom: 26, left: 44 };

/** Part-to-whole over time: stacked bands separated by a 2px surface gap. */
export function StackedArea({ data, labels, colors, format, height = 180, max = 1 }: Props) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const innerW = Math.max(40, width - M.left - M.right);
  const totalH = M.top + height + M.bottom;

  const geom = useMemo(() => {
    if (data.length < 2) return null;
    const x = linearScale(data[0].t, data[data.length - 1].t, M.left, M.left + innerW);
    const y = linearScale(0, max, M.top + height, M.top);
    const bands = labels.map((_, k) => {
      const top = data.map((d) => [x(d.t), y(d.values.slice(0, k + 1).reduce((a, b) => a + b, 0))] as [number, number]);
      const bottom = data.map((d) => [x(d.t), y(d.values.slice(0, k).reduce((a, b) => a + b, 0))] as [number, number]);
      const path =
        top.map(([px, py], i) => `${i ? 'L' : 'M'}${px.toFixed(1)},${py.toFixed(1)}`).join('') +
        bottom
          .reverse()
          .map(([px, py]) => `L${px.toFixed(1)},${py.toFixed(1)}`)
          .join('') +
        'Z';
      const edge = top.map(([px, py], i) => `${i ? 'L' : 'M'}${px.toFixed(1)},${py.toFixed(1)}`).join('');
      return { path, edge };
    });
    return { x, y, bands, tt: timeTicks(data[0].t, data[data.length - 1].t, Math.max(2, Math.floor(innerW / 110))) };
  }, [data, labels, innerW, height, max]);

  if (!geom) {
    return (
      <div ref={ref} className="chart-empty" style={{ height: totalH }}>
        Waiting for the first council session…
      </div>
    );
  }

  const onMove = (e: PointerEvent<SVGSVGElement>) => {
    const px = e.clientX - e.currentTarget.getBoundingClientRect().left;
    if (px < M.left || px > M.left + innerW) return setHover(null);
    setHover(nearestIndex(data, geom.x.invert(px)));
  };
  const h = hover != null ? data[hover] : null;

  return (
    <div ref={ref} className="viz" style={{ height: totalH }}>
      <svg width={width} height={totalH} role="img" aria-label="Stacked share over time" onPointerMove={onMove} onPointerLeave={() => setHover(null)}>
        {[0, 0.25, 0.5, 0.75, 1].map((v) => (
          <g key={v}>
            <line className="grid" x1={M.left} x2={M.left + innerW} y1={geom.y(v * max)} y2={geom.y(v * max)} />
            <text className="tick" x={M.left - 8} y={geom.y(v * max)} dy="0.32em" textAnchor="end">
              {format(v * max)}
            </text>
          </g>
        ))}
        {geom.bands.map((b, k) => (
          <path key={labels[k]} d={b.path} fill={colors[k]} fillOpacity={0.82} />
        ))}
        {geom.bands.slice(0, -1).map((b, k) => (
          <path key={`e${k}`} d={b.edge} fill="none" stroke="var(--surface)" strokeWidth={2} />
        ))}
        {geom.tt.ticks.map((t) => (
          <text key={t} className="tick" x={geom.x(t)} y={totalH - 8} textAnchor="middle">
            {fmtTime(t, geom.tt.step)}
          </text>
        ))}
        {h && <line className="crosshair" x1={geom.x(h.t)} x2={geom.x(h.t)} y1={M.top} y2={M.top + height} />}
      </svg>
      {h && (
        <Tooltip x={geom.x(h.t)} y={M.top + 10} width={width}>
          {labels
            .map((l, k) => ({ l, k }))
            .reverse()
            .map(({ l, k }) => (
              <span key={l} className="tt-row">
                <i className="tt-key" style={{ background: colors[k] }} />
                <strong>{format(h.values[k])}</strong> {l}
              </span>
            ))}
          <span className="tt-time">{fmtDateTime(h.t)}</span>
        </Tooltip>
      )}
    </div>
  );
}
